import {Box, Pagination} from '@mantine/core';
import Head from 'next/head';
import {useRouter} from 'next/router';
import React, {useEffect} from 'react';
import {GoogleAdBannerName} from '../../../../config/constants/googleAdBanner';
import {GSSKT_HOST} from '../../../../config/constants/hosts';
import {GoogleAdBanner} from '../../../../shared/components/Banner/GoogleAdBanner';
import {useGoogleAdBanner} from '../../../../shared/services/useGoogleAdBanner';
import {
  type RecruitmentOrderType,
  useHandleRecruitingInfoForPc,
} from '../../usecase/useHandleRecruitingInfoForPC';
import {CardForPc} from './Presenter/CardForPc';
import {CardSkelton} from './Presenter/CardSkelton';
import {HeaderForPC} from './Presenter/HeaderForPC';
import {LeftColumn} from './Presenter/LeftColumn';

type Props = Record<string, never>;

const limit = 30;

export const RecruitingInfoForPc: React.FC<Props> = () => {
  const {
    data,
    isLoading,
    industryCategories,
    filterCategories,
    filterRecruitmentTags,
    filterRecruitmentTypes,
    filterMasterGroupedJobTypes,
    filterGraduationYear,
    setFilterCategories,
    setFilterRecruitmentTags,
    setFilterRecruitmentTypes,
    setFilterMasterGroupedJobTypes,
    setRecruitmentOrder,
    setFilterGraduationYear,
    recruitmentTags,
    recruitmentTypes,
    masterGroupedJobTypes,
    graduationYearsHots,
    currentPage,
    setCurrentPage,
    lastPage,
  } = useHandleRecruitingInfoForPc({
    contentsLimit: limit,
  });

  // DFPバナーのセット
  const googleAdBannerNames = [
    GoogleAdBannerName.RICH_DESKTOP_HEADER,
    GoogleAdBannerName.B_RECTANGLE_BOTTOM,
    GoogleAdBannerName.D_FEED_LEFT_SMALL,
    GoogleAdBannerName.B_RECTANGLE,
  ];
  useGoogleAdBanner(googleAdBannerNames);
  const baseUrl = `${GSSKT_HOST}/recruiting_info`;

  const canonicalUrl = `${baseUrl}`;

  const router = useRouter();
  const {order, page} = router.query;

  useEffect(() => {
    setRecruitmentOrder(order as RecruitmentOrderType);

    if (page) {
      setCurrentPage(Number(page));
    }
  }, [order, page]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  return (
    <>
      <Head>
        <title key="title">募集を探す | 外資就活ドットコム</title>
        <meta key="og:title" property="og:title" content="募集を探す" />
        <meta key="og:url" property="og:url" content={canonicalUrl} />
        <meta
          key="og:description"
          property="og:description"
          content="募集を探す際はこちら。外資・日系トップ企業を目指す学生のための就職活動サイト「外資就活ドットコム」。トップ企業の新卒採用情報から、インターンシップ・先輩のエントリーシート・ES・体験記・就活コラム・TOEIC対策まで多様なコンテンツを掲載。"
        />
        <meta
          name="description"
          key="description"
          content="募集を探す際はこちら。外資・日系トップ企業を目指す学生のための就職活動サイト「外資就活ドットコム」。トップ企業の新卒採用情報から、インターンシップ・先輩のエントリーシート・ES・体験記・就活コラム・TOEIC対策まで多様なコンテンツを掲載。"
        />
        <meta
          key="keywords"
          name="keywords"
          content="外資,就活,インターンシップ,新卒採用,会社説明会,就活コラム,ES・体験記"
        />
        <link rel="canonical" href={canonicalUrl} key="canonical" />
      </Head>
      <Box className="py-8">
        <GoogleAdBanner
          googleAdBannerName={GoogleAdBannerName.RICH_DESKTOP_HEADER}
        />
      </Box>
      <HeaderForPC pageTitle="募集を探す" />
      <Box className="w-[1200px] flex items-start space-x-16">
        <Box className="sticky top-[-1000px] flex w-[400px] flex-col gap-y-16">
          <LeftColumn
            industryCategories={industryCategories}
            recruitmentTags={recruitmentTags}
            recruitmentTypes={recruitmentTypes}
            masterGroupedJobTypes={masterGroupedJobTypes}
            graduationYearsHots={graduationYearsHots}
            filterCategories={filterCategories}
            filterRecruitmentTags={filterRecruitmentTags}
            filterRecruitmentTypes={filterRecruitmentTypes}
            filterGroupedJobTypes={filterMasterGroupedJobTypes}
            filterGraduationYear={filterGraduationYear}
            setFilterCategories={setFilterCategories}
            setFilterRecruitmentTags={setFilterRecruitmentTags}
            setFilterRecruitmentTypes={setFilterRecruitmentTypes}
            setFilterGroupedJobTypes={setFilterMasterGroupedJobTypes}
            setRecruitmentOrder={setRecruitmentOrder}
            setFilterGraduationYear={setFilterGraduationYear}
          />
          <Box className="">
            <GoogleAdBanner
              googleAdBannerName={GoogleAdBannerName.D_FEED_LEFT_SMALL}
            />
          </Box>
        </Box>
        <Box className="flex flex-col w-full">
          <Box className="flex flex-col relative">
            {(data?.recruitments?.length ?? 0) ? (
              <>
                {data?.recruitments.map((recruitment, recruitmentIndex) => (
                  <React.Fragment key={recruitmentIndex}>
                    {recruitment && (
                      <CardForPc
                        key={recruitment?.id}
                        recruitment={recruitment}
                        className="mb-16"
                      />
                    )}
                  </React.Fragment>
                ))}
                <Pagination
                  className="mx-auto"
                  value={currentPage}
                  total={lastPage}
                  onChange={(value: number) => {
                    handlePageChange(value);
                  }}
                />
              </>
            ) : isLoading ? (
              <>
                {Array.from({length: limit}).map((_, i) => (
                  <CardSkelton key={i} />
                ))}
              </>
            ) : (
              <Box className="bg-white mb-[10px] px-20 py-[10px] text-base text-black-1">
                一致する募集情報は見つかりませんでした。
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </>
  );
};
